import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormErrorMessage,
    FormHelperText,
    FormLabel,
    Heading,
    Text,
    VStack,
} from '@chakra-ui/react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { UpdateSettingsFormValues, zUserSettings } from '@acp/shared';
import { useSession } from '@acp/feature/auth';
import { PropertyManagerSelect } from '../../agency';
import { useSettingsUpdate } from '../hooks/use-settings-update';
import { useUserSettings } from '../hooks/use-user-settings';
import { usePortfolioMirroring } from '../hooks/use-portfolio-mirroring';

export function MyPreferences() {
    const { data: settings, isLoading } = useUserSettings();
    const { complianceAgencyName } = useSession();
    const portfolioMirroring = usePortfolioMirroring();

    const defaultPropertyManagerId = useMemo(() => {
        if (settings === undefined) {
            return null;
        }

        return settings.agency?.defaultPropertyManagerId;
    }, [settings, isLoading]);

    const showPortfolioMirroring = useMemo(() => {
        if (settings === undefined) {
            return undefined;
        }

        return settings.agency?.showPortfolioMirroring;
    }, [settings, isLoading]);

    const form = useForm<UpdateSettingsFormValues>({
        resolver: zodResolver(zUserSettings),
        defaultValues: {
            agency: {
                defaultPropertyManagerId,
                showPortfolioMirroring,
            },
        },
        mode: 'onChange',
    });

    const {
        handleSubmit,
        reset,
        formState: { isDirty },
    } = form;

    const updateMutation = useSettingsUpdate({
        onSuccess: (updatedSettings) => {
            reset({
                agency: {
                    defaultPropertyManagerId: updatedSettings.agency.defaultPropertyManagerId,
                    showPortfolioMirroring: updatedSettings.agency.showPortfolioMirroring,
                },
            });
        },
    });

    const onSubmit = (values: UpdateSettingsFormValues) => {
        updateMutation.update(values);
    };

    return (
        <>
            <Heading size="md">My preferences</Heading>
            <VStack maxW={['initial', null, null, 'full']} align="left">
                <VStack gap={10}>
                    <Controller
                        name="agency.defaultPropertyManagerId"
                        control={form.control}
                        render={({ field, fieldState }) => (
                            <FormControl isInvalid={fieldState.invalid}>
                                <FormLabel htmlFor="none" fontWeight="bold">
                                    Preset dashboard filters to this property manager
                                </FormLabel>
                                <FormHelperText color="muted.500">
                                    <>
                                        <Text>
                                            This property manager will be selected
                                            by default on the dashboard.
                                        </Text>
                                        <Text>
                                            Note that you can still use the property manager
                                            filter to refine results.
                                        </Text>
                                    </>
                                </FormHelperText>
                                <Box w="fit-content" mt={4} minW="70%">
                                    <PropertyManagerSelect
                                        onChange={(option) => field.onChange(
                                            option !== null ? Number(option.value) : null
                                        )}
                                        value={field.value?.toString() ?? null}
                                    />
                                </Box>
                                <FormHelperText color="muted.500">
                                    This setting is for you and only specific for
                                    {' '}
                                    {complianceAgencyName}
                                    .
                                </FormHelperText>
                                <FormErrorMessage>
                                    {fieldState.error?.message}
                                </FormErrorMessage>
                            </FormControl>
                        )}
                    />
                    {
                        portfolioMirroring.showSetting && (
                            <Controller
                                name="agency.showPortfolioMirroring"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                    <FormControl isInvalid={fieldState.invalid}>
                                        <Checkbox
                                            isChecked={field.value}
                                            onChange={(e) => field.onChange(e.target.checked)}
                                            fontWeight="bold"
                                        >
                                            Show properties on service with other providers
                                        </Checkbox>
                                        <FormHelperText color="muted.500">
                                            <>
                                                <Text>
                                                    Properties not on Detector Inspector&apos;s
                                                    services will be shown in the portfolio.
                                                </Text>
                                                <Text>
                                                    The dashboard will also have enhanced
                                                    statistics about properties not on
                                                    Detector Inspector&apos;s services.
                                                </Text>
                                                <Text color="muted.500">
                                                    This setting is for you and only specific for
                                                    {' '}
                                                    {complianceAgencyName}
                                                    .
                                                </Text>
                                            </>
                                        </FormHelperText>
                                        <FormErrorMessage>
                                            {fieldState.error?.message}
                                        </FormErrorMessage>
                                    </FormControl>
                                )}
                            />
                        )
                    }
                </VStack>

                <Button
                    w="fit-content"
                    mt={12}
                    onClick={handleSubmit(onSubmit)}
                    isDisabled={!isDirty}
                    isLoading={updateMutation.isPending}
                >
                    Save changes
                </Button>
            </VStack>
        </>
    );
}
